import React from 'react';
import { Link } from 'react-router-dom';
import { useThrottledToast } from '../../hooks/useThrottledToast';
import ReactMarkdown from 'react-markdown';

import { ArrowLeft } from '../../components/icons/arrow-left';
import { Switch } from '../../components/common/switch';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { selectUser, setUserData } from '../../store/auth.slice';
import { useChangeUserMutation } from '../../services/users.api';
import { useTariffToolCheck } from '../../hooks/useTariffToolCheck';
import popovaImage from '../../assets/images/clouds/popova.jpg';
import cloudImage from '../../assets/images/clouds/cloud.png';

const CLOUDS_METHODOLOGY_MARKDOWN = `

Методика представляет собой инновационный синтез:

1. **Научно-обоснованной проективной техники**, интегрирующей принципы экзистенциально-феноменологического подхода и арт-терапии.
2. **Цифровой трансформации** классического арт-терапевтического процесса.
3. **Глубокой платформенной интеграции**, создающей новое качество терапевтической сессии за счёт аналитики и долгосрочного мониторинга.

---

## 1. Теоретико-методологический фундамент

Методология базируется на трёх взаимодополняющих основаниях:

• **Проективная парадигма:** Аморфные стимулы (облака) активируют механизм проекции, позволяя внутреннему содержанию психики (конфликтам, образам, переживаниям) проецироваться на внешний объект, обеспечивая безопасный доступ к глубинным процессам

• **Экзистенциально-феноменологический подход:** Акцент на непосредственном, «наивном» восприятии и спонтанно рождающихся образах позволяет выходить на экзистенциальные темы (свобода, изоляция, смысл, тревога) в обход психологических защит.

• **Арт-терапевтический подход:** Процедура последовательно реализует этапы арт-терапевтического творчества: от активации бессознательного содержания через проекцию, его осознания и вербализацию — до возможной трансформации и интеграции в целостный нарратив.

**Нейропсихологическое обоснование:** Работа с визуальными образами целенаправленно активирует правополушарные процессы, отвечающие за интуитивное, метафорическое мышление и доступ к имплицитной (процедурной и эмоциональной) памяти. Это создаёт условия для реконсолидации эмоционально значимых воспоминаний в терапевтическом контексте.

Травматические переживания, сильные эмоции часто "хранятся" в невербальных зонах мозга (правое полушарие, лимбическая система, особенно амигдала). Речевые центры (зона Брока, Вернике) находятся преимущественно в левом полушарии. Когда слова недоступны (при стрессе, травме), творчество (рисунок, лепка, танец) становится прямым каналом к этим переживаниям, минуя речевые барьеры. Арт-процесс задействует оба полушария: правое (образы, метафоры, эмоции, пространственное восприятие) и левое (структура, планирование композиции, смысл). Создание арт-объекта и позже вербализация способствуют созданию новых нейронных связей между полушариями. Это основа для эффективной работы с травмирующим опытом.

---

## 2. Структура и терапевтическая логика методики (3 этапа)

1. **Активация и проекция (Выбор и обведение):** Спонтанный выбор одного из 50 изображений облаков служит невербальным индикатором актуального эмоционального состояния. Последующее моторное действие — обведение увиденного образа — телесно закрепляет проекцию, усиливая связь с бессознательным материалом.

2. **Эксплорация и осознание (Исследование образа):** через серию открытых вопросов («Что это?», «Какое оно?») образ исследуется, вербализируется и наполняется личными смыслами. На этом этапе у клиента происходит важный сдвиг от пассивного восприятия к активному диалогу со своим Я.

3. **Интеграция и трансформация (Работа с образом):** Используя техники сократического диалога устанавливается связь между образом и актуальной жизненной ситуацией. Возможность дорисовать, изменить образ на экране становится мощным инструментом для проживания трансформации и интеграции новых смыслов в личную историю.

**Терапевтические механизмы:**

• **Эффект дистанцирования и безопасности:** Работа с метафорой («образ в облаках») снижает сопротивление и позволяет обсуждать сложные темы.

• **Символическая репрезентация:** Невозможное для вербализации переживание находит точное выражение в зримом символе.

• **Диалогический принцип:** Пространство для встречи сознательной позиции клиента и бессознательного содержания, где терапевт выступает в роли фасилитатора-исследователя.

---

## 3. Уникальные преимущества цифровой реализации на платформе ZiNGiZi

Цифровой формат не дублирует, а радикально усиливает арт-терапевтическую методику, вводя новые измерения в терапевтическую работу:

1. **Система объективного мониторинга динамики (Терапевтический журнал):**
    Все созданные образы автоматически сохраняются с привязкой к дате и этапу терапии, формируя уникальную визуальную ленту прогресса.
    Психолог получает возможность проводить сравнительный анализ образов во времени, отслеживая эволюцию тем, цветовой палитры, сложности проекций, что служит объективным маркером изменений.

2. **Аналитическая поддержка и преодоление «слепых пятен» (ИИ-ассистент):**
    Встроенные алгоритмы анализируют проективный материал, помогая выявлять повторяющиеся символы, паттерны и неочевидные связи между сессиями.
    Инструмент предоставляет психологу аналитические гипотезы и темы для рефлексии, усиливая его профессиональное восприятие, но не заменяя клиническое мышление.

3. **Научно-методологическая инновация: квантификация субъективного:**
    Цифровая среда впервые позволяет переводить глубинный субъективный опыт в анализируемые данные: время выбора, скорость рисования, цветовые предпочтения, динамика изменения образов.
    Это создает основы для научных исследований и формирования доказательной базы в области арт-терапевтических и проективных методов и открывает новые перспективы для изучения психотерапевтического процесса

---

## 4. Заключение: Новая парадигма проективной терапии

Инструмент «Облака» в реализации платформы ZiNGiZi представляет собой качественный прорыв, устанавливающий новый стандарт цифровой проективной терапии.

Это синергия глубины и точности: глубина классического проективного метода соединяется с точностью цифровой аналитики. Это альянс спонтанности и системности: творческая экспрессия клиента обогащается возможностью системного отслеживания динамики. Это усиление, а не замена: интуиция и профессионализм терапевта получают в лице платформы мощного аналитического союзника.

Таким образом, инструмент «Облака» — это не «оцифрованная» методика, а рождение новой терапевтической среды, которая:

• Повышает эффективность арт-терапии за счёт объективного мониторинга и аналитической поддержки.
• Открывает новые горизонты для научных исследований в психотерапии.
• Формирует принципиально новый, живой и многомерный формат терапевтической документации.

Инструмент «Облака» наглядно демонстрирует, что современные технологии могут служить не заменой человеческого контакта, а его мощным усилителем, расширяя возможности для понимания, изменения и роста.

---

**Автор методологии:** Татьяна Анатольевна Попова, к.психол.н.

**Цифровая реализация и платформенная интеграция:** ZiNGiZi

© Все права на методику и ее цифровую реализацию защищены. Использование в коммерческих или профессиональных целях без лицензирования запрещено.`;

const Clouds = () => {
  const tool = 'clouds';
  const isActiveByTariff = useTariffToolCheck(tool);
  const dispatch = useAppDispatch();
  const userData = useAppSelector(selectUser);
  const [changeUser] = useChangeUserMutation();

  const showToast = useThrottledToast();

  const handleCheck = async () => {
    try {
      if (!isActiveByTariff) return;
      if (!userData) return;
      if (userData?.active_psy_tools?.includes(tool)) {
        const arr = userData?.active_psy_tools.filter((el) => el !== tool);
        dispatch(setUserData({ ...userData, active_psy_tools: arr }));
        await changeUser({
          id: userData.id,
          active_psy_tools: arr,
        });
      } else {
        dispatch(
          setUserData({
            ...userData,
            active_psy_tools: [...userData?.active_psy_tools, tool],
          }),
        );
        await changeUser({
          id: userData.id,
          active_psy_tools: [...userData?.active_psy_tools, tool],
        });
      }
      showToast('success');
    } catch (error) {
      showToast('error');
    }
  };

  return (
    <div className="pb-[80px]">
      <h2 className="flex items-center gap-[14px] font-comfortaa font-semibold text-xl text-blackBase mb-5">
        <Link to={'/tools'} className="group">
          <ArrowLeft className="fill-blackBase group-hover:fill-redBase" width={21} height={16} />
        </Link>
        <span>Облака</span>
      </h2>

      <div className="w-full mb-10">
        <div className="text-blackBase font-manrope text-sm leading-relaxed">
          <h2 className="text-xl font-semibold text-blackBase mb-4">
            Методологическое обоснование авторского инструмента «Облака» на цифровой терапевтической платформе ZiNGiZi
          </h2>

          {/* Секция с автором */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-blackBase mb-4">Авторство и научно-практический контекст</h3>
            <div className="flex items-start gap-6">
              <div className="flex-shrink-0">
                <img
                  src={popovaImage}
                  alt="Татьяна Анатольевна Попова"
                  className="w-[150px] h-[210px] rounded-[12px] object-cover"
                />
              </div>
              <div className="flex-1 text-greyBase text-sm leading-relaxed">
                <p>
                  Инструмент «Облака» является плодом сотворчества <strong>Татьяны Анатольевны Поповой</strong> —
                  кандидата психологических наук, доцента МИП, практикующего специалиста по арт-терапии, супервизора,
                  автора идеи и разработчика направления «Артлоготерапия», доцента кафедры «Психотерапия качества
                  жизни», научного руководителя магистратуры по арт-терапии НОЧУ ВО Московский институт психоанализа,
                  старшего научного сотрудника ФНБНУ «Федеральный научный центр психологических и междисциплинарных
                  исследований» и цифровой платформы ZiNGiZi.
                </p>
              </div>
            </div>
          </div>

          <ReactMarkdown
            components={{
              h2: (props) => <h2 className="text-xl font-semibold text-blackBase mt-8 mb-4 first:mt-0" {...props} />,
              h3: (props) => <h3 className="text-lg font-semibold text-blackBase mt-6 mb-3" {...props} />,
              p: (props) => <p className="mb-4 last:mb-0 text-greyBase" {...props} />,
              ul: (props) => <ul className="list-disc pl-6 mb-4 space-y-2" {...props} />,
              ol: (props) => <ol className="list-decimal pl-6 mb-4 space-y-2" {...props} />,
              li: (props) => <li className="text-greyBase" {...props} />,
              hr: () => <hr className="my-8 border-t border-greyBase" />,
              strong: (props) => <strong className="font-semibold text-blackBase" {...props} />,
            }}
          >
            {CLOUDS_METHODOLOGY_MARKDOWN}
          </ReactMarkdown>
        </div>
      </div>
      <div className="flex items-center gap-5 mb-5">
        <Switch
          checked={userData?.active_psy_tools?.includes(tool)}
          disabled={!isActiveByTariff}
          setChecked={() => handleCheck()}
          label={'Использовать Облака в видео-сессиях'}
        />
      </div>

      <div className="w-full mb-[40px]">
        <img src={cloudImage} alt="Пример облака" className="w-full max-w-full rounded-[12px]" />
      </div>
    </div>
  );
};

export default Clouds;
